import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SettingsService } from '../core/settings/settings.service';
import { MenuService } from '../core/menu/menu.service';
export interface Project {
  id: string;
  name: string;
  status: string;
  color: string;
  purpose: string;
  grants: Array<{ module: string; source: string; capability: string; expiresAt: string }>;
}
@Injectable({ providedIn: 'root' })
export class PlatformService {
  session: { user: { id: string; name: string; administrator: boolean; role?: string; administrativeCapabilities?: string[] }; projects: Project[] } | null = null;
  selected = '';
  error = '';
  constructor(
    private http: HttpClient,
    public settings: SettingsService,
    private menu: MenuService,
    private router: Router
  ) {}
  get base() {
    return this.settings.configUso.urlApiPadrao + 'v1/platform';
  }
  get headers() {
    return { 'X-Auth-Token': this.settings.user.authToken || '' };
  }
  get project() {
    return this.session?.projects.find((p) => p.id === this.selected);
  }
  get canManagePlatform() {
    return !!this.session?.user.administrativeCapabilities?.includes('platform.catalog.read');
  }
  has(project: string, capability: string) {
    return !!this.session?.projects.find((p) => p.id === project)?.grants.some((g) => g.capability === capability);
  }
  async refresh() {
    if (!this.settings.user.authToken) {
      try {
        this.settings.user = JSON.parse(sessionStorage.getItem('AUTH') || localStorage.getItem('AUTH') || '{}');
      } catch {
        this.clear();
      }
    }
    try {
      this.session = await firstValueFrom(this.http.get<any>(this.base + '/session', { headers: this.headers }));
      this.settings.logado = true;
      if (!this.session.projects.some((p) => p.id === this.selected))
        this.selected = this.session.projects[0]?.id || '';
      this.navigation();
      return this.session;
    } catch (e) {
      this.clear();
      throw e;
    }
  }
  navigation() {
    const items: any[] = [{ text: 'Projetos', link: '/projetos', icon: 'icon-grid' }];
    if (this.canManagePlatform)
      items.push({ text: 'Administração', link: '/administracao', icon: 'icon-settings' });
    if (this.selected === 'sogesp' && this.has('sogesp', 'project.metrics.read'))
      items.push({ text: 'SOGESP', link: '/sogesp', icon: 'icon-chart' });
    if (this.selected === 'game-cardio' && this.has('game-cardio', 'project.metrics.read'))
      items.push({ text: 'Game Cardio', link: '/game-cardio-admin', icon: 'icon-chart' });
    if (this.selected === 'shared-services') {
      if (this.has(this.selected, 'communications.read'))
        items.push(
          { text: 'Comunicações', link: '/comunicacoes/clientes', icon: 'icon-envelope' },
          { text: 'Lotes de mensagens', link: '/lotes-mensagens', icon: 'icon-list' }
        );
      if (this.has(this.selected, 'waf.read'))
        items.push({ text: 'WAF', link: '/waf-access-blocks', icon: 'icon-shield' });
    }
    this.menu.addMenu(items);
  }
  async select(id: string) {
    await this.refresh();
    if (!this.session.projects.some((p) => p.id === id)) return;
    this.selected = id;
    this.navigation();
    await this.router.navigateByUrl('/projetos');
  }
  async login(login: string, password: string, remember: boolean) {
    const normalized = login.replace(/[.\-\s]/g, '');
    const user = await firstValueFrom(this.http.post<any>(this.settings.configUso.urlApiPadrao + 'v2/hotsite/login', { cpf: normalized, senha: password }));
    this.clear();
    this.settings.user = user;
    (remember ? localStorage : sessionStorage).setItem('AUTH', JSON.stringify(user));
    await this.refresh();
    await this.router.navigateByUrl('/projetos');
  }
  async logout() {
    this.error = '';
    try {
      await firstValueFrom(this.http.post(this.base + '/logout', {}, { headers: this.headers }));
      this.clear();
      await this.router.navigateByUrl('/login');
    } catch {
      this.error = 'Não foi possível encerrar a sessão no servidor. Tente novamente.';
    }
  }
  clear() {
    this.session = null;
    this.selected = '';
    this.settings.user = {};
    this.settings.logado = false;
    localStorage.removeItem('AUTH');
    sessionStorage.removeItem('AUTH');
    this.menu.addMenu([]);
  }
  get<T = any>(path: string) {
    return firstValueFrom(this.http.get<T>(this.base + path, { headers: this.headers }));
  }
  post(path: string, body: any) {
    return firstValueFrom(this.http.post<any>(this.base + path, body, { headers: this.headers }));
  }
}
